import { Check, CheckCheck, CircleAlert, Clock } from 'lucide-react';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import type { WhatsappMessage } from '@/services/endpoints/whatsappInbox';
import { clockTime } from '../inboxUtils';
import { MessageMedia } from './MessageMedia';

// role='img' so the aria-label of these icon spans is actually announced
const Mark: FC<{ label: string; title?: string; className?: string; children: ReactNode }> = ({ label, title = label, className, children }) => (
  <span role='img' title={title} aria-label={label} className={className}>
    {children}
  </span>
);

const StatusMark: FC<Pick<WhatsappMessage, 'status' | 'error'>> = ({ status, error }) => {
  const { t } = useTranslation('whatsappInbox');
  const icon = 'size-3.5';
  switch (status) {
    case 'failed':
      // a white chip: red on the brand bubble would not be readable
      return (
        <Mark
          label={t('status.failed')}
          title={error || t('status.failed')}
          className='flex items-center gap-0.5 rounded bg-white-100 px-1 font-semibold text-red-600'
        >
          <CircleAlert className={icon} />
          {t('status.failed')}
        </Mark>
      );
    case 'read':
      return (
        <Mark label={t('status.read')} className='text-cyan-300'>
          <CheckCheck className={icon} />
        </Mark>
      );
    case 'delivered':
      return (
        <Mark label={t('status.delivered')}>
          <CheckCheck className={icon} />
        </Mark>
      );
    case 'sent':
    case 'enqueued':
      return (
        <Mark label={t('status.sent')}>
          <Check className={icon} />
        </Mark>
      );
    default:
      return (
        <Mark label={t('status.sending')}>
          <Clock className='size-3' />
        </Mark>
      );
  }
};

/** `grouped`: follows a message from the same side, so it sits closer and keeps all its corners round. */
export const MessageBubble: FC<{ message: WhatsappMessage; grouped?: boolean }> = ({ message, grouped = false }) => {
  const { t } = useTranslation('whatsappInbox');
  const outbound = message.direction === 'outbound';

  return (
    <li className={twMerge('flex', outbound ? 'justify-end' : 'justify-start', grouped ? 'mt-0.5' : 'mt-3')}>
      <div
        className={twMerge(
          'max-w-[70%] space-y-1 rounded-[10px] px-2.5 py-1.5 text-sm shadow-sm',
          outbound ? 'bg-brand-600 text-white-100' : 'bg-white-100 text-primary-900',
          !grouped && (outbound ? 'rounded-tr-sm' : 'rounded-tl-sm')
        )}
      >
        {message.media ? <MessageMedia message={message} /> : null}
        {message.text ? <p className='whitespace-pre-wrap break-words'>{message.text}</p> : null}
        {!message.text && !message.media ? <p className='italic opacity-70'>{t('unsupported')}</p> : null}
        <div className={twMerge('flex items-center justify-end gap-1 text-[11px]', outbound ? 'text-white-80' : 'text-primary-900/50')}>
          <span>{clockTime(message.sentAt)}</span>
          {outbound ? <StatusMark status={message.status} error={message.error} /> : null}
        </div>
      </div>
    </li>
  );
};
